import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../hooks/useAuth';
import { useWebSocket } from '../hooks/useWebSocket';
import { useLanguage } from '../context/LanguageContext';
import { portfolio, market, analytics } from '../services/api';
import { TradingViewWidget } from '../components/TradingViewWidget';
import { HoldingsTable } from '../components/HoldingsTable';
import { PerformanceStats } from '../components/PerformanceStats';
import { HistoricalCharts } from '../components/HistoricalCharts';
import { AlertsPanel } from '../components/AlertsPanel';
import { EmailImportPanel } from '../components/EmailImportPanel';
import { UserManagement } from '../components/UserManagement';
import { AdvancedPanel } from '../components/AdvancedPanel';
import { ModuleManagement } from '../components/ModuleManagement';
import { InterestingDeals } from '../components/InterestingDeals';
import { DepthChart } from '../components/DepthChart';
import { ProbabilityLattice } from '../components/ProbabilityLattice';
import { TailProbabilityRidge } from '../components/TailProbabilityRidge';
import { AssetCorrelationNetwork } from '../components/AssetCorrelationNetwork';
import { MonteCarloSimulation } from '../components/MonteCarloSimulation';
import { TransactionUploadLog } from '../components/TransactionUploadLog';
import { InvestmentForecaster } from '../components/InvestmentForecaster';
import { RebalancingAdvisor } from '../components/RebalancingAdvisor';
import { EfficientFrontier } from '../components/EfficientFrontier';
import { TaxLossHarvester } from '../components/TaxLossHarvester';
import { MacroStressTester } from '../components/MacroStressTester';
import { NewsSentiment } from '../components/NewsSentiment';
import { WhatIfSandbox } from '../components/WhatIfSandbox';
import { CorrelationHeatmap } from '../components/CorrelationHeatmap';
import { BacktestingEngine } from '../components/BacktestingEngine';
import { FxImpactTracker } from '../components/FxImpactTracker';
import { DividendTracker } from '../components/DividendTracker';
import { MacroSensitivity } from '../components/MacroSensitivity';
import { RiskAlertsPanel } from '../components/RiskAlertsPanel';
import { ImapCrawlLog } from '../components/ImapCrawlLog';
import { PdfReportGenerator } from '../components/PdfReportGenerator';
import { TransactionLedger } from '../components/TransactionLedger';
import { AssetDirectory } from '../components/AssetDirectory';
import { PortfolioOverviewHub } from '../components/PortfolioOverviewHub';
import { BenchmarkComparison } from '../components/BenchmarkComparison';
import { LiveMarketTickerBar } from '../components/LiveMarketTickerBar';
import { AiPortfolioHealthInsights } from '../components/AiPortfolioHealthInsights';
import { InteractiveAssetVisualizer } from '../components/InteractiveAssetVisualizer';
import { TaxProfitStatementGenerator } from '../components/TaxProfitStatementGenerator';
import { DividendRetirementPlanner } from '../components/DividendRetirementPlanner';
import { LiveSentimentNewsImpactScanner } from '../components/LiveSentimentNewsImpactScanner';
import { MacroStressTestEngine } from '../components/MacroStressTestEngine';
import { MultiBrokerVaultManager } from '../components/MultiBrokerVaultManager';
import { AutoWeeklyPdfDigestConfig } from '../components/AutoWeeklyPdfDigestConfig';
import { SharpeSortinoRiskEngine } from '../components/SharpeSortinoRiskEngine';
import { InstallPwaPrompt } from '../components/InstallPwaPrompt';
import { AdminConfigHub } from '../components/AdminConfigHub';
import { LiveMarketSentimentHub } from '../components/LiveMarketSentimentHub';
import { Asset, Holding, Alert, Order } from '../types';
import { api, ordersApi } from '../services/api';
import { soundManager } from '../utils/soundFeedback';

export const Dashboard: React.FC = () => {
  const { user, logout } = useAuth();
  const isAdmin = (user?.role || '').toUpperCase() === 'ADMIN';
  const { language, setLanguage, t } = useLanguage();
  const [assets, setAssets] = useState<Asset[]>([]);
  const [holdings, setHoldings] = useState<Holding[]>([]);
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [stats, setStats] = useState<any>(null);
  const [enabledModules, setEnabledModules] = useState<Record<string, boolean>>({});
  const [selectedSymbol, setSelectedSymbol] = useState('AAPL');
  const [activeTab, setActiveTab] = useState<'trading' | 'charts' | 'sentiment' | 'quant' | 'wealth' | 'admin'>('trading');

  useEffect(() => {
    if (!isAdmin && activeTab === 'admin') {
      setActiveTab('trading');
    }
  }, [isAdmin, activeTab]);
  const [toasts, setToasts] = useState<{ id: string; message: string; type: string }[]>([]);
  const [showExplanationModal, setShowExplanationModal] = useState(false);
  const [manualModalTab, setManualModalTab] = useState<'features' | 'ios' | 'android' | 'desktop'>('features');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState<boolean>(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('sidebar_collapsed') === 'true';
    }
    return false;
  });

  const toggleSidebar = () => {
    setIsSidebarCollapsed(prev => {
      const next = !prev;
      localStorage.setItem('sidebar_collapsed', String(next));
      return next;
    });
  };

  const [theme, setTheme] = useState(() => localStorage.getItem('theme') || 'classic');

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('theme', theme);
  }, [theme]);

  // Global Power User Hotkey: Esc to close modals and mobile menu
  useEffect(() => {
    const handleGlobalEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setShowExplanationModal(false);
        setMobileMenuOpen(false);
      }
    };
    window.addEventListener('keydown', handleGlobalEsc);
    return () => window.removeEventListener('keydown', handleGlobalEsc);
  }, []);

  const [cdpStatus, setCdpStatus] = useState<'connected' | 'disconnected' | 'checking'>('checking');
  const [eurUsdRate, setEurUsdRate] = useState(1.09);
  const [lastSyncTime, setLastSyncTime] = useState<Date>(new Date());
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Global Markets Status (US, Europe, Asia) with local market context (CSG in Prague & Amsterdam)
  const globalMarkets = useMemo(() => {
    const now = new Date();
    const utcHours = now.getUTCHours();
    const utcMins = now.getUTCMinutes();
    const day = now.getUTCDay(); // 0 is Sun, 6 is Sat
    const isWeekend = day === 0 || day === 6;
    const currentMin = utcHours * 60 + utcMins;

    // 1. Europe (Frankfurt XETRA / Euronext Amsterdam / BCPP Prague): 
    // Continuous trading: 07:00 - 15:30 UTC (09:00 - 17:30 CET)
    // Pre-market auction / opening: 06:00 - 07:00 UTC (08:00 - 09:00 CET)
    const isEuOpen = !isWeekend && currentMin >= 7 * 60 && currentMin < 15 * 60 + 30;
    const isEuPreMarket = !isWeekend && currentMin >= 6 * 60 && currentMin < 7 * 60;

    // 2. US (NYSE / NASDAQ): 
    // Regular trading: 13:30 - 20:00 UTC (15:30 - 22:00 CET)
    // After-hours / Extended: 20:00 - 24:00 UTC (22:00 - 02:00 CET)
    const isUsOpen = !isWeekend && currentMin >= 13 * 60 + 30 && currentMin < 20 * 60;
    const isUsAfterHours = !isWeekend && currentMin >= 20 * 60 && currentMin < 24 * 60;

    // 3. Asia (Tokyo TSE / Hong Kong HKEX): 
    // 00:00 - 06:30 UTC (02:00 - 08:30 CET)
    const isAsiaOpen = (day >= 1 && day <= 5) && currentMin >= 0 && currentMin < 6 * 60 + 30;

    return {
      eu: { 
        isOpen: isEuOpen, 
        isPre: isEuPreMarket,
        label: isEuOpen ? 'EU 🟢 09:00-17:30' : isEuPreMarket ? 'EU 🟡 PRE-MKT' : 'EU ⚪ ZATVORENÉ', 
        title: 'Európa: Euronext Amsterdam & BCPP Praha (CSG), XETRA Frankfurt (09:00 - 17:30 CET)' 
      },
      us: { 
        isOpen: isUsOpen, 
        isAfter: isUsAfterHours, 
        label: isUsOpen ? 'US 🟢 15:30-22:00' : isUsAfterHours ? 'US 🟡 AFTER-HRS' : 'US ⚪ ZATVORENÉ', 
        title: 'USA: NYSE, NASDAQ (15:30 - 22:00 CET)' 
      },
      asia: { 
        isOpen: isAsiaOpen, 
        label: isAsiaOpen ? 'ASIA 🟢 02:00-08:30' : 'ASIA ⚪ ZATVORENÉ', 
        title: 'Ázia: Tokyo TSE, Hong Kong HKEX (02:00 - 08:30 CET)' 
      }
    };
  }, []);

  // Portfolio Value and Weights Calculation (Unifying on EUR baseline using live FX rate)
  // Portfolio Value and Weights Calculation (Unifying on EUR baseline using live FX rate)
  const totalPortfolioValue = useMemo(() => {
    if (!Array.isArray(holdings)) return 0;
    return holdings.filter(h => Number(h.quantity) > 0).reduce((sum, h) => {
      const isEur = h.currency === 'EUR';
      const rawPrice = Number(h.current_price || h.avg_cost || 0);
      const priceInEur = isEur ? rawPrice : rawPrice / eurUsdRate;
      return sum + (Number(h.quantity || 0) * priceInEur);
    }, 0);
  }, [holdings, eurUsdRate]);

  const holdingsWithWeights = useMemo(() => {
    if (!Array.isArray(holdings) || totalPortfolioValue === 0) return [];
    return holdings.filter(h => Number(h.quantity) > 0).map(h => {
      const isEur = h.currency === 'EUR';
      const price = h.current_price || h.avg_cost || 0;
      const priceInEur = isEur ? price : price / eurUsdRate;
      const val = h.quantity * priceInEur;
      return {
        ...h,
        value: val,
        weight: (val / totalPortfolioValue) * 100
      };
    }).sort((a, b) => b.value - a.value);
  }, [holdings, totalPortfolioValue, eurUsdRate]);

  // Simulation Mode States
  const [isSimulationMode, setIsSimulationMode] = useState(false);
  const [simulatedTrades, setSimulatedTrades] = useState<{ symbol: string; type: 'BUY' | 'SELL'; quantity: number; price: number }[]>([]);
  const [priceShock, setPriceShock] = useState(0);
  const [isCommitting, setIsCommitting] = useState(false);
  const [portfolioTab, setPortfolioTab] = useState<'overview' | 'buys' | 'prices' | 'calendar' | 'exports'>('overview');

  // Compute Simulated Holdings reactively
  const simulatedHoldings = useMemo(() => {
    const safeHoldings = Array.isArray(holdings) ? holdings : [];
    if (!isSimulationMode) return safeHoldings;
    const safeAssets = Array.isArray(assets) ? assets : [];
    const safeTrades = Array.isArray(simulatedTrades) ? simulatedTrades : [];

    // 1. Copy holdings and apply price shocks
    let result: Holding[] = safeHoldings.map(h => {
      const originalPrice = h.current_price || h.avg_cost || 0;
      const shockedPrice = originalPrice * (1 + priceShock / 100);
      return {
        ...h,
        current_price: shockedPrice
      };
    });

    // 2. Apply simulated trades
    safeTrades.forEach(trade => {
      const existingIdx = result.findIndex(h => h.symbol === trade.symbol);
      if (existingIdx !== -1) {
        const h = result[existingIdx];
        const newQty = trade.type === 'BUY' ? h.quantity + trade.quantity : Math.max(0, h.quantity - trade.quantity);
        
        let newAvgCost = h.avg_cost;
        if (trade.type === 'BUY' && (h.quantity + trade.quantity) > 0) {
          newAvgCost = (h.quantity * h.avg_cost + trade.quantity * trade.price) / (h.quantity + trade.quantity);
        }

        result[existingIdx] = {
          ...h,
          quantity: newQty,
          avg_cost: newAvgCost
        };
      } else {
        // Find asset in assets list
        const asset = safeAssets.find(a => a.symbol === trade.symbol);
        if (asset) {
          const shockedPrice = trade.price * (1 + priceShock / 100);
          result.push({
            id: `sim-${trade.symbol}`,
            asset_id: asset.id,
            quantity: trade.quantity,
            avg_cost: trade.price,
            symbol: trade.symbol,
            name: asset.name,
            current_price: shockedPrice,
            exchange: asset.exchange
          });
        }
      }
    });

    // Filter out zero quantities
    return result.filter(h => h.quantity > 0);
  }, [holdings, isSimulationMode, simulatedTrades, priceShock, assets, user]);

  const simulatedPortfolioValue = useMemo(() => {
    const safeSimHoldings = Array.isArray(simulatedHoldings) ? simulatedHoldings : [];
    return safeSimHoldings.reduce((sum, h) => {
      const isEur = h.currency === 'EUR';
      const rawPrice = Number(h.current_price || h.avg_cost || 0);
      const priceInEur = isEur ? rawPrice : rawPrice / eurUsdRate;
      return sum + (Number(h.quantity || 0) * priceInEur);
    }, 0);
  }, [simulatedHoldings, eurUsdRate]);

  const simulatedHoldingsWithWeights = useMemo(() => {
    const safeSimHoldings = Array.isArray(simulatedHoldings) ? simulatedHoldings : [];
    if (safeSimHoldings.length === 0 || simulatedPortfolioValue === 0) return [];
    return safeSimHoldings.map(h => {
      const isEur = h.currency === 'EUR';
      const rawPrice = Number(h.current_price || h.avg_cost || 0);
      const priceInEur = isEur ? rawPrice : rawPrice / eurUsdRate;
      const val = Number(h.quantity || 0) * priceInEur;
      return {
        ...h,
        value: val,
        weight: (val / simulatedPortfolioValue) * 100
      };
    }).sort((a, b) => b.value - a.value);
  }, [simulatedHoldings, simulatedPortfolioValue, eurUsdRate]);

  // Compute Simulated Stats
  const simulatedStats = useMemo(() => {
    if (!stats) return null;
    const safeSimHoldings = Array.isArray(simulatedHoldings) ? simulatedHoldings : [];
    const safeTrades = Array.isArray(simulatedTrades) ? simulatedTrades : [];
    const safeHoldings = Array.isArray(holdings) ? holdings : [];
    if (!isSimulationMode) return stats;

    let totalInvested = 0;
    let currentValue = 0;
    let realizedDiff = 0;

    safeSimHoldings.forEach(h => {
      const isEur = h.currency === 'EUR';
      const price = h.current_price || h.avg_cost || 0;
      const priceInEur = isEur ? price : price / eurUsdRate;
      const avgCostInEur = isEur ? h.avg_cost : h.avg_cost / eurUsdRate;

      currentValue += h.quantity * priceInEur;
      totalInvested += h.quantity * avgCostInEur;
    });

    safeTrades.forEach(trade => {
      if (trade.type === 'SELL') {
        const originalHolding = safeHoldings.find(h => h.symbol === trade.symbol);
        if (originalHolding) {
          const isEur = originalHolding.currency === 'EUR';
          const tradePriceInEur = isEur ? trade.price : trade.price / eurUsdRate;
          const avgCostInEur = isEur ? originalHolding.avg_cost : originalHolding.avg_cost / eurUsdRate;
          realizedDiff += trade.quantity * (tradePriceInEur - avgCostInEur);
        }
      }
    });

    const unrealized = currentValue - totalInvested;
    const realized = stats.realized + realizedDiff;
    const totalReturn = realized + unrealized;

    const valRatio = stats.currentValue > 0 ? currentValue / stats.currentValue : 1;
    const returnShock = priceShock * 0.05;
    const simulatedSharpe = Math.max(0.1, stats.sharpe * (1 + returnShock) * valRatio);
    const simulatedVar = Math.max(0.5, stats.var95 * (1 - priceShock / 150));
    const simulatedMaxDd = Math.max(0.5, stats.maxDrawdown * (1 - priceShock / 150));

    return {
      ...stats,
      totalInvested,
      currentValue,
      unrealized,
      realized,
      totalReturn,
      sharpe: Math.round(simulatedSharpe * 100) / 100,
      var95: Math.round(simulatedVar * 100) / 100,
      maxDrawdown: Math.round(simulatedMaxDd * 100) / 100
    };
  }, [stats, isSimulationMode, simulatedHoldings, simulatedTrades, holdings, priceShock, eurUsdRate]);

  // Commit Simulated trades
  const handleCommitTrades = async () => {
    setIsCommitting(true);
    try {
      for (const t of simulatedTrades) {
        const asset = assets.find(a => a.symbol === t.symbol);
        if (!asset) continue;
        if (t.type === 'BUY') {
          await portfolio.buy({ assetId: asset.id, quantity: t.quantity, price: t.price });
        } else {
          await portfolio.sell({ assetId: asset.id, quantity: t.quantity, price: t.price });
        }
      }
      addToast(
        language === 'sk' ? 'Simulované obchody boli úspešne zrealizované!' : 'Simulated trades successfully executed!',
        'success'
      );
      setSimulatedTrades([]);
      setIsSimulationMode(false);
      fetchDashboardData();
    } catch (err: any) {
      addToast(err.response?.data?.error || 'Failed to commit simulated trades', 'error');
    } finally {
      setIsCommitting(false);
    }
  };

  const addToast = useCallback((message: string, type: string = 'info') => {
    const id = Math.random().toString(36).substring(7);
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, 8000);
  }, []);

  const checkCdpHealth = useCallback(async () => {
    try {
      const res = await api.get('/tradingview/health');
      if (res.data.status === 'connected') {
        setCdpStatus('connected');
      } else {
        setCdpStatus('disconnected');
      }
    } catch {
      setCdpStatus('disconnected');
    }
  }, []);

  const handleSymbolChange = useCallback(async (symbol: string) => {
    setSelectedSymbol(symbol);
    try {
      await api.post('/tradingview/action', { action: 'switch', symbol });
      addToast(`TradingView switched to ${symbol}`, 'success');
      checkCdpHealth();
    } catch {
      console.warn('TradingView CDP sync not active');
    }
  }, [addToast, checkCdpHealth]);

  const handleSelectDeal = (symbol: string) => {
    handleSymbolChange(symbol);
    setActiveTab('charts');
  };

  const [pendingOrders, setPendingOrders] = useState<Order[]>([]);

  const fetchOrdersData = useCallback(async () => {
    try {
      const res = await ordersApi.getOrders();
      if (res.data?.orders && Array.isArray(res.data.orders)) {
        const pending = res.data.orders.filter((o: Order) => o.status === 'PENDING');
        setPendingOrders(prev => {
          // Play discrete institutional chime if new pending order arrived
          if (pending.length > prev.length && prev.length >= 0) {
            soundManager.playOrderChime();
          }
          return pending;
        });
      }
    } catch (e) {
      // non-blocking
    }
  }, []);

  const fetchDashboardData = useCallback(async () => {
    if (!user) return;
    try {
      const [assetsRes, holdingsRes, alertsRes, statsRes, modulesRes, fxRes] = await Promise.all([
        market.getAssets(),
        portfolio.getHoldings(),
        market.getAlerts(),
        analytics.getPerformance(),
        api.get('/modules'),
        market.getFxRate().catch(() => ({ data: { rate: 1.09 } }))
      ]);
      setAssets(Array.isArray(assetsRes.data) ? assetsRes.data : []);
      setHoldings(Array.isArray(holdingsRes.data) ? holdingsRes.data : []);
      setAlerts(Array.isArray(alertsRes.data) ? alertsRes.data : []);
      setStats(statsRes.data || null);

      if (fxRes && fxRes.data && fxRes.data.rate) {
        setEurUsdRate(fxRes.data.rate);
      }

      const modsMap: Record<string, boolean> = {};
      if (Array.isArray(modulesRes.data)) {
        modulesRes.data.forEach((m: any) => {
          modsMap[m.module_name] = m.is_enabled;
        });
      }
      setEnabledModules(modsMap);

      const safeHoldings = Array.isArray(holdingsRes.data) ? holdingsRes.data : [];
      if (safeHoldings.length > 0) {
        const hasAapl = safeHoldings.some((h: any) => h.symbol === 'AAPL');
        if (!hasAapl) {
          setSelectedSymbol(safeHoldings[0].symbol);
        }
      }

      fetchOrdersData();
      setLastSyncTime(new Date());
    } catch (err: any) {
      console.error('Failed to load dashboard data', err);
      if (err.response?.status === 401) {
        logout();
      }
    } finally {
      setIsRefreshing(false);
    }
  }, [user, logout, fetchOrdersData]);

  const triggerManualSync = async () => {
    setIsRefreshing(true);
    await fetchDashboardData();
  };

  // Periodic order poll for real-time trader notifications (every 10s)
  useEffect(() => {
    fetchOrdersData();
    const interval = setInterval(fetchOrdersData, 10000);
    return () => clearInterval(interval);
  }, [fetchOrdersData]);

  const { prices, connected } = useWebSocket(
    user?.tenantId,
    useCallback((data: any) => {
      addToast(data.message, 'success');
      soundManager.playOrderChime();
      fetchDashboardData();
    }, [fetchDashboardData, addToast]),
    useCallback((alertData: any) => {
      addToast(
        `Cenové upozornenie pre ${alertData.symbol}: limit ${alertData.type === 'above' ? '≥' : '≤'} ${alertData.threshold} dosiahnutý (Aktuálne: ${alertData.price})`,
        'alert'
      );
      soundManager.playAlertPing();
      fetchDashboardData();
    }, [fetchDashboardData, addToast])
  );




  useEffect(() => {
    fetchDashboardData();
    checkCdpHealth();
  }, [fetchDashboardData, checkCdpHealth]);

  // Merge real‑time prices into assets
  useEffect(() => {
    if (Array.isArray(prices) && prices.length > 0) {
      setAssets(prev => {
        if (!Array.isArray(prev)) return [];
        return prev.map(asset => {
          const updated = Array.isArray(prices) ? prices.find((p: any) => p.id === asset.id) : undefined;
          return updated ? { ...asset, current_price: Number(updated.current_price), last_updated: updated.last_updated } : asset;
        });
      });

      // Also update prices in holdings
      setHoldings(prev => {
        if (!Array.isArray(prev)) return [];
        return prev.map(holding => {
          const updated = Array.isArray(prices) ? prices.find((p: any) => p.id === holding.asset_id) : undefined;
          return updated ? { ...holding, current_price: Number(updated.current_price) } : holding;
        });
      });
    }
  }, [prices]);

  const handleTradeSuccess = () => {
    fetchDashboardData();
  };

  const handleAlertChange = () => {
    market.getAlerts().then(res => setAlerts(res.data));
  };

  return (
    <div className="min-h-screen bg-[var(--bg-primary)] text-[var(--text-primary)] font-sans flex flex-col relative overflow-hidden">
      {/* Live Market Telemetry Bar */}
      <LiveMarketTickerBar holdings={simulatedHoldings} />

      <div className="flex flex-col lg:flex-row flex-1 relative overflow-hidden">

      {/* Mobile Top Bar */}
      <div className="lg:hidden w-full bg-[var(--bg-secondary)] border-b border-[var(--border-color)] p-3.5 flex justify-between items-center z-40 shrink-0 font-mono shadow-sm">
        <h1 className="text-base font-extrabold tracking-tight flex items-center gap-2">
          <img src="/assets/anogi_logo.png" alt="Anogi" className="h-6 w-auto object-contain drop-shadow-sm" />
          <span className="text-gradient">Anogi Traders</span>
          <span className="text-[8px] px-1.5 py-0.5 bg-[var(--accent-color)] border border-[var(--border-color)] text-[var(--text-primary)] rounded uppercase font-black">Live</span>
        </h1>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setMobileMenuOpen(prev => !prev)}
            className="flex items-center gap-1.5 px-3 py-1.5 border border-[var(--border-color)] bg-[var(--accent-color)] hover:bg-[var(--accent-hover)] active:scale-95 rounded-xl text-xs font-black transition-all shadow-sm"
            aria-label="Toggle navigation menu"
          >
            {mobileMenuOpen ? (
              <><span>✕</span> <span>CLOSE</span></>
            ) : (
              <><span>☰</span> <span>MENU</span></>
            )}
          </button>
        </div>
      </div>

      {/* LEFT SIDEBAR NAVIGATION (COLLAPSIBLE) */}
      <aside className={`w-full ${isSidebarCollapsed ? 'lg:w-20 px-3' : 'lg:w-64 p-6'} bg-[var(--bg-secondary)] border-b lg:border-b-0 lg:border-r border-[var(--border-color)] lg:flex flex-col justify-between shrink-0 font-mono text-xs transition-all duration-300 ease-in-out ${mobileMenuOpen ? 'flex' : 'hidden'}`}>
        <div className="space-y-6">
          {/* Logo / Brand + Sidebar Toggle Button */}
          <div className="hidden lg:flex items-center justify-between gap-2">
            {!isSidebarCollapsed ? (
              <div className="flex items-center gap-3 min-w-0">
                <img src="/assets/anogi_logo.png" alt="Anogi" className="h-8 w-auto object-contain drop-shadow-sm shrink-0" />
                <div className="flex flex-col gap-0.5 min-w-0">
                  <span className="text-base font-extrabold tracking-tight text-gradient truncate">Anogi Traders</span>
                  <span className="w-fit text-[8px] px-1.5 py-0.5 bg-[var(--accent-color)] border border-[var(--border-color)] text-[var(--text-primary)] rounded font-mono uppercase tracking-widest">Dashboard</span>
                </div>
              </div>
            ) : (
              <div className="w-full flex justify-center pb-1">
                <img src="/assets/anogi_logo.png" alt="Anogi" className="h-8 w-8 object-contain drop-shadow-sm" />
              </div>
            )}

            {/* Desktop Sidebar Collapse Toggle Button */}
            <button
              onClick={toggleSidebar}
              className="p-1.5 rounded-xl border border-[var(--border-color)] bg-[var(--accent-color)] hover:bg-[var(--accent-hover)] text-stone-600 hover:text-stone-900 transition-all shrink-0"
              title={isSidebarCollapsed ? t("expandSidebar") : t("collapseSidebar")}
            >
              <span className="text-xs font-bold">{isSidebarCollapsed ? '▶' : '◀'}</span>
            </button>
          </div>

          {/* Navigation Links with Concise Names & Tooltips */}
          <nav className="flex flex-col gap-1.5">
            <button
              onClick={() => { setActiveTab('trading'); setMobileMenuOpen(false); }}
              className={`flex items-center ${isSidebarCollapsed ? 'justify-center p-2.5' : 'gap-3 px-4 py-2.5'} rounded-xl text-sm font-semibold transition-all border ${
                activeTab === 'trading'
                  ? 'bg-[var(--text-primary)] text-[var(--bg-primary)] border-[var(--text-primary)] shadow-sm'
                  : 'text-[var(--text-secondary)] border-transparent hover:bg-[var(--accent-color)] hover:text-[var(--text-primary)]'
              }`}
              title={t("navPortfolio")}
            >
              <span className="text-lg">💼</span>
              {!isSidebarCollapsed && <span>{t("navPortfolio")}</span>}
            </button>

            <button
              onClick={() => { setActiveTab('charts'); setMobileMenuOpen(false); }}
              className={`flex items-center ${isSidebarCollapsed ? 'justify-center p-2.5' : 'gap-3 px-4 py-2.5'} rounded-xl text-sm font-semibold transition-all border ${
                activeTab === 'charts'
                  ? 'bg-[var(--text-primary)] text-[var(--bg-primary)] border-[var(--text-primary)] shadow-sm'
                  : 'text-[var(--text-secondary)] border-transparent hover:bg-[var(--accent-color)] hover:text-[var(--text-primary)]'
              }`}
              title={t("navCharts")}
            >
              <span className="text-lg">📈</span>
              {!isSidebarCollapsed && <span>{t("navCharts")}</span>}
            </button>

            <button
              onClick={() => { setActiveTab('sentiment'); setMobileMenuOpen(false); }}
              className={`flex items-center ${isSidebarCollapsed ? 'justify-center p-2.5' : 'gap-3 px-4 py-2.5'} rounded-xl text-sm font-semibold transition-all border ${
                activeTab === 'sentiment'
                  ? 'bg-[var(--text-primary)] text-[var(--bg-primary)] border-[var(--text-primary)] shadow-sm'
                  : 'text-[var(--text-secondary)] border-transparent hover:bg-[var(--accent-color)] hover:text-[var(--text-primary)]'
              }`}
              title={t("navSentiment")}
            >
              <span className="text-lg">📰</span>
              {!isSidebarCollapsed && <span>{t("navSentiment")}</span>}
            </button>

            <button
              onClick={() => { setActiveTab('quant'); setMobileMenuOpen(false); }}
              className={`flex items-center ${isSidebarCollapsed ? 'justify-center p-2.5' : 'gap-3 px-4 py-2.5'} rounded-xl text-sm font-semibold transition-all border ${
                activeTab === 'quant'
                  ? 'bg-[var(--text-primary)] text-[var(--bg-primary)] border-[var(--text-primary)] shadow-sm'
                  : 'text-[var(--text-secondary)] border-transparent hover:bg-[var(--accent-color)] hover:text-[var(--text-primary)]'
              }`}
              title={t("navQuant")}
            >
              <span className="text-lg">🏛️</span>
              {!isSidebarCollapsed && <span>{t("navQuant")}</span>}
            </button>

            <button
              onClick={() => { setActiveTab('wealth'); setMobileMenuOpen(false); }}
              className={`flex items-center ${isSidebarCollapsed ? 'justify-center p-2.5' : 'gap-3 px-4 py-2.5'} rounded-xl text-sm font-semibold transition-all border ${
                activeTab === 'wealth'
                  ? 'bg-[var(--text-primary)] text-[var(--bg-primary)] border-[var(--text-primary)] shadow-sm'
                  : 'text-[var(--text-secondary)] border-transparent hover:bg-[var(--accent-color)] hover:text-[var(--text-primary)]'
              }`}
              title={t("navWealth")}
            >
              <span className="text-lg">💎</span>
              {!isSidebarCollapsed && <span>{t("navWealth")}</span>}
            </button>



            {isAdmin && (
              <button
                onClick={() => { setActiveTab('admin'); setMobileMenuOpen(false); }}
                className={`flex items-center ${isSidebarCollapsed ? 'justify-center p-2.5' : 'gap-3 px-4 py-2.5'} rounded-xl text-sm font-semibold transition-all border ${
                  activeTab === 'admin'
                    ? 'bg-[var(--text-primary)] text-[var(--bg-primary)] border-[var(--text-primary)] shadow-sm'
                    : 'text-[var(--text-secondary)] border-transparent hover:bg-[var(--accent-color)] hover:text-[var(--text-primary)]'
                }`}
                title={t("navAdmin")}
              >
                <span className="text-lg">🛡️</span>
                {!isSidebarCollapsed && <span>{t("navAdmin")}</span>}
              </button>
            )}
          </nav>
        </div>

        {/* User Account bottom block */}
        <div className={`mt-auto pt-4 border-t border-[var(--border-color)]/70 ${isSidebarCollapsed ? 'space-y-3' : 'space-y-3'}`}>
          {/* User Profile Card */}
          <div className={`flex items-center ${isSidebarCollapsed ? 'justify-center' : 'justify-between px-3 py-2 bg-slate-50/80 rounded-2xl border border-slate-200/80 shadow-2xs'}`}>
            <div className="flex items-center gap-2.5 min-w-0">
              <div 
                className="w-9 h-9 rounded-xl bg-slate-900 border border-slate-700 flex items-center justify-center font-black text-white text-xs shadow-xs shrink-0" 
                title={user?.fullName || 'User'}
              >
                {user?.fullName?.charAt(0) || 'U'}
              </div>
              {!isSidebarCollapsed && (
                <div className="text-left min-w-0 truncate">
                  <div className="text-xs font-black text-slate-900 truncate tracking-tight">{user?.fullName || 'User'}</div>
                  <div className="text-[9px] text-slate-500 font-bold uppercase tracking-wider">{user?.role || 'TRADER'}</div>
                </div>
              )}
            </div>

            {!isSidebarCollapsed && (
              <span className="w-2 h-2 rounded-full bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.6)] shrink-0" title="Account active" />
            )}
          </div>

          <div className="flex flex-col gap-1.5 font-mono">
            {/* Live Feed Status & Language Pill Switcher */}
            <div className={`flex items-center ${isSidebarCollapsed ? 'justify-center' : 'justify-between px-2 py-1'}`}>
              {!isSidebarCollapsed && (
                <div className="flex items-center gap-1.5 text-[11px] font-bold text-slate-600">
                  <span className={`w-2 h-2 rounded-full ${connected ? 'bg-emerald-500 shadow-[0_0_6px_rgba(16,185,129,0.5)]' : 'bg-rose-500 animate-pulse'}`}></span>
                  <span>{connected ? t("liveFeed") : t("offline")}</span>
                </div>
              )}

              {/* Language Switcher Segmented Control */}
              <div className="flex items-center gap-0.5 bg-slate-100 p-0.5 rounded-xl border border-slate-300/80 shadow-inner">
                <button
                  onClick={() => setLanguage('en')}
                  className={`px-2 py-1 rounded-lg text-[10px] font-black transition-all ${
                    language === 'en' 
                      ? 'bg-slate-900 text-white shadow-xs' 
                      : 'text-slate-600 hover:text-slate-950'
                  }`}
                  title="Switch to English"
                >
                  EN
                </button>
                <button
                  onClick={() => setLanguage('sk')}
                  className={`px-2 py-1 rounded-lg text-[10px] font-black transition-all ${
                    language === 'sk' 
                      ? 'bg-slate-900 text-white shadow-xs' 
                      : 'text-slate-600 hover:text-slate-950'
                  }`}
                  title="Prepnúť do Slovenčiny"
                >
                  SK
                </button>
              </div>
            </div>

            {/* Application Manual & Mobile Setup Button */}
            <button
              onClick={() => {
                setManualModalTab('features');
                setShowExplanationModal(true);
              }}
              className={`w-full flex items-center ${
                isSidebarCollapsed ? 'justify-center p-2.5' : 'justify-between px-3 py-2'
              } text-xs font-bold text-slate-800 bg-white hover:bg-slate-50 border border-slate-200 hover:border-slate-300 rounded-xl transition-all shadow-2xs group overflow-hidden`}
              title={t("explanationBtn")}
            >
              <div className="flex items-center gap-2 min-w-0 flex-1 mr-2">
                <span className="text-sm shrink-0">📖</span>
                {!isSidebarCollapsed && (
                  <span className="font-extrabold truncate text-left text-[11px] text-slate-800">{t("explanationBtn")}</span>
                )}
              </div>
              {!isSidebarCollapsed && (
                <span className="text-slate-400 shrink-0 group-hover:text-slate-900 group-hover:translate-x-0.5 transition-all text-xs font-black">
                  →
                </span>
              )}
            </button>

            {/* Logout Button */}
            <button
              onClick={logout}
              className={`w-full flex items-center ${
                isSidebarCollapsed ? 'justify-center p-2.5' : 'justify-between px-3 py-2'
              } text-xs font-bold text-rose-600 hover:text-rose-700 bg-rose-50/60 hover:bg-rose-100/70 border border-rose-200/80 hover:border-rose-300 rounded-xl transition-all shadow-2xs group`}
              title={t("logout")}
            >
              <div className="flex items-center gap-2">
                <span className="text-sm">🚪</span>
                {!isSidebarCollapsed && (
                  <span className="font-extrabold uppercase tracking-wider text-[11px]">{t("logout")}</span>
                )}
              </div>
              {!isSidebarCollapsed && (
                <span className="text-rose-400 group-hover:text-rose-700 group-hover:translate-x-0.5 transition-all text-xs font-black">
                  →
                </span>
              )}
            </button>
          </div>
        </div>
      </aside>

      {/* MAIN CONTENT AREA WITH MOBILE SAFE AREA SPACING */}
      <main className="flex-1 p-3 sm:p-5 lg:p-6 space-y-4 overflow-y-auto max-h-screen pb-24 lg:pb-6 touch-pan-y">
        
        {/* INSTITUTIONAL TOP-BAR: Market Status, Telemetry Sync, and Unified Notification Hub */}
        <header className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 p-3 bg-white/85 backdrop-blur-md border border-[var(--border-color)] rounded-2xl shadow-xs font-mono text-xs">
          {/* Left: Active Section & Real-time Market Status */}
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <span className="text-base">💼</span>
              <span className="font-extrabold text-slate-900 tracking-tight text-sm uppercase">
                {activeTab === 'trading' && t("navPortfolio")}
                {activeTab === 'charts' && t("navCharts")}
                {activeTab === 'sentiment' && t("navSentiment")}
                {activeTab === 'quant' && t("navQuant")}
                {activeTab === 'wealth' && t("navWealth")}
                {activeTab === 'admin' && t("navAdmin")}
              </span>
            </div>

            <span className="text-stone-300 hidden sm:inline">•</span>

            {/* Global Market Status Pills (EU, US, ASIA) */}
            <div className="flex items-center gap-1.5 flex-wrap">
              <span 
                className={`px-2 py-0.5 rounded-lg text-[9px] font-black border uppercase tracking-wider ${
                  globalMarkets.eu.isOpen ? 'bg-emerald-50 text-emerald-800 border-emerald-300' : 'bg-slate-100 text-slate-500 border-slate-200'
                }`}
                title={globalMarkets.eu.title}
              >
                {globalMarkets.eu.label}
              </span>

              <span 
                className={`px-2 py-0.5 rounded-lg text-[9px] font-black border uppercase tracking-wider ${
                  globalMarkets.us.isOpen 
                    ? 'bg-emerald-50 text-emerald-800 border-emerald-300' 
                    : globalMarkets.us.isAfter 
                    ? 'bg-amber-50 text-amber-800 border-amber-300' 
                    : 'bg-slate-100 text-slate-500 border-slate-200'
                }`}
                title={globalMarkets.us.title}
              >
                {globalMarkets.us.label}
              </span>

              <span 
                className={`px-2 py-0.5 rounded-lg text-[9px] font-black border uppercase tracking-wider ${
                  globalMarkets.asia.isOpen ? 'bg-emerald-50 text-emerald-800 border-emerald-300' : 'bg-slate-100 text-slate-500 border-slate-200'
                }`}
                title={globalMarkets.asia.title}
              >
                {globalMarkets.asia.label}
              </span>
            </div>

            {/* Live FX Rate EUR/USD Pill */}
            <div className="hidden md:flex items-center gap-1 px-2.5 py-1 bg-slate-100 rounded-xl text-[10px] font-black text-slate-700 border border-slate-200">
              <span className="text-slate-400">FX €/$:</span>
              <span>{eurUsdRate.toFixed(4)}</span>
            </div>
          </div>

          {/* Right: Last Sync Time + Manual Sync Button + Universal Notification Bell */}
          <div className="flex items-center gap-2.5 self-end sm:self-auto">
            {/* Last Updated Timestamp & Quick Sync */}
            <button
              onClick={triggerManualSync}
              disabled={isRefreshing}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-50 hover:bg-slate-100 active:scale-95 border border-slate-300 text-slate-700 hover:text-slate-900 rounded-xl font-black text-[10px] transition-all shadow-xs disabled:opacity-50"
              title={t("syncData")}
            >
              <span className={`${isRefreshing ? 'animate-spin inline-block text-indigo-600' : 'text-slate-500'}`}>🔄</span>
              <span>
                {isRefreshing 
                  ? (language === 'sk' ? 'Synchronizujem...' : 'Syncing...')
                  : `${t("lastUpdated")}: ${lastSyncTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}`}
              </span>
            </button>
          </div>
        </header>

        {/* PWA Mobile Installation Prompt Banner */}
        <InstallPwaPrompt />

        {/* TAB 1: PORTFOLIO & ACTIVE HOLDINGS */}
        {activeTab === 'trading' && (
          <div className="space-y-4 animate-[fadeIn_0.2s_ease-out]">
            <PortfolioOverviewHub 
              user={user}
              holdings={holdings}
              simulatedHoldings={simulatedHoldings}
              assets={assets}
              stats={simulatedStats}
              onSymbolSelect={handleSymbolChange}
              onTrade={(symbol) => {
                handleSymbolChange(symbol);
                setActiveTab('charts');
              }}
              onRefresh={fetchDashboardData}
              activeSubTab={portfolioTab}
              onTabChange={setPortfolioTab}
              enabledModules={enabledModules}
              eurUsdRate={eurUsdRate}
            />

            {/* Multi-Account & Multi-Broker Vault Manager */}
            <MultiBrokerVaultManager holdings={holdings} />

          </div>
        )}

        {/* TAB 2: CHARTS & MARKET TRENDS */}
        {activeTab === 'charts' && (
          <div className="space-y-4 animate-[fadeIn_0.2s_ease-out]">
            {/* Real-time TradingView Candlestick Chart */}
            <div className="glass-panel p-5 space-y-3">
              <div className="flex justify-between items-center pb-2 border-b border-[var(--border-color)]">
                <div>
                  <h3 className="text-sm font-extrabold uppercase tracking-widest text-[#1c1917] flex items-center gap-2">
                    <span>📊</span> {language === 'sk' ? 'Živý Burzový Graf (TradingView Pro)' : 'Live Interactive Candlestick Chart'}
                  </h3>
                  <p className="text-[10px] text-stone-500 font-mono mt-0.5">
                    {language === 'sk' ? `Aktuálny ticker: ${selectedSymbol}` : `Active instrument: ${selectedSymbol}`}
                  </p>
                </div>

                {/* Quick Ticker Switcher */}
                <div className="flex items-center gap-1">
                  {['CSG', 'WDC', 'SNDK', 'LITE', 'MU', 'AAPL'].map(sym => (
                    <button
                      key={sym}
                      onClick={() => handleSymbolChange(sym)}
                      className={`px-2.5 py-1 rounded-lg text-xs font-black transition-all ${
                        selectedSymbol === sym ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                      }`}
                    >
                      {sym}
                    </button>
                  ))}
                </div>
              </div>

              <div className="h-[420px] rounded-2xl overflow-hidden border border-slate-200">
                <TradingViewWidget symbol={selectedSymbol} />
              </div>
            </div>

            {/* Historical Trend Series & Technical Overlays */}
            <HistoricalCharts />
          </div>
        )}

        {/* TAB: REAL-TIME NEWS & AI SENTIMENT TRACKER (INDEPENDENT SECTION) */}
        {activeTab === 'sentiment' && (
          <div className="space-y-4 animate-[fadeIn_0.2s_ease-out]">
            <LiveMarketSentimentHub
              holdings={simulatedHoldings}
              onTrade={(symbol) => {
                handleSymbolChange(symbol);
                setActiveTab('charts');
              }}
              onOpenChart={(symbol) => {
                handleSymbolChange(symbol);
                setActiveTab('charts');
              }}
            />
          </div>
        )}

        {/* TAB 3: QUANTITATIVE & RISK ENGINE */}
        {activeTab === 'quant' && (
          <div className="space-y-4 animate-[fadeIn_0.2s_ease-out]">
            {/* Sharpe & Sortino Quantitative Risk Engine */}
            <SharpeSortinoRiskEngine holdings={simulatedHoldings} />

            {/* AI Portfolio Health Insights & Risk Calculator */}
            <AiPortfolioHealthInsights holdings={simulatedHoldings} assets={assets} />

            {/* Target Allocation Rebalancing Execution Wizard */}
            <RebalancingAdvisor holdings={simulatedHoldings} />

            {/* Real-Time Macro Stress-Test Engine */}
            <MacroStressTestEngine holdings={simulatedHoldings} />

            {/* Quantitative Simulations Accordion */}
            <div className="space-y-3 font-mono">
              <details className="glass-panel p-4 rounded-2xl group" open>
                <summary className="font-extrabold text-xs uppercase tracking-wider cursor-pointer flex justify-between items-center text-[var(--text-primary)]">
                  <span className="flex items-center gap-2"><span>🏛️</span> VIP BENCHMARK ENGINE & ALFA METRIKY</span>
                  <span className="text-stone-400 group-open:rotate-180 transition-transform">▼</span>
                </summary>
                <div className="mt-4">
                  <BenchmarkComparison holdings={simulatedHoldings} />
                </div>
              </details>

              <details className="glass-panel p-4 rounded-2xl group">
                <summary className="font-extrabold text-xs uppercase tracking-wider cursor-pointer flex justify-between items-center text-[var(--text-primary)]">
                  <span className="flex items-center gap-2"><span>📈</span> PROBABILITY LATTICE & TAIL PROBABILITY RIDGE</span>
                  <span className="text-stone-400 group-open:rotate-180 transition-transform">▼</span>
                </summary>
                <div className="mt-4 space-y-4">
                  <ProbabilityLattice symbol={selectedSymbol} holdings={simulatedHoldings} assets={assets} />
                  <TailProbabilityRidge symbol={selectedSymbol} holdings={simulatedHoldings} assets={assets} />
                </div>
              </details>

              <details className="glass-panel p-4 rounded-2xl group">
                <summary className="font-extrabold text-xs uppercase tracking-wider cursor-pointer flex justify-between items-center text-[var(--text-primary)]">
                  <span className="flex items-center gap-2"><span>🎲</span> MONTE CARLO SIMULÁCIA & PROJEKCIA VÝNOSOV</span>
                  <span className="text-stone-400 group-open:rotate-180 transition-transform">▼</span>
                </summary>
                <div className="mt-4 space-y-4">
                  <AssetCorrelationNetwork holdings={simulatedHoldings} />
                  <MonteCarloSimulation holdings={simulatedHoldings} selectedSymbol={selectedSymbol} />
                  <InvestmentForecaster holdings={simulatedHoldings} selectedSymbol={selectedSymbol} />
                </div>
              </details>
            </div>
          </div>
        )}

        {/* TAB 4: WEALTH PLANNING & TAX HUB */}
        {activeTab === 'wealth' && (
          <div className="space-y-4 animate-[fadeIn_0.2s_ease-out]">
            {/* Tax & Profit Realization Statement Generator */}
            <TaxProfitStatementGenerator holdings={simulatedHoldings} />

            {/* Dividend & Retirement Passive Income Planner */}
            <DividendRetirementPlanner holdings={simulatedHoldings} />

            {/* Automated Weekly PDF Email Digest Config */}
            <AutoWeeklyPdfDigestConfig />

            {/* PDF Report Generator */}
            <PdfReportGenerator holdings={simulatedHoldings} stats={simulatedStats} />
          </div>
        )}



        {activeTab === 'admin' && isAdmin && (
          <div className="max-w-6xl mx-auto space-y-4">
            <AdminConfigHub />
            <EmailImportPanel />
            <ModuleManagement />
            <UserManagement />
          </div>
        )}
      </main>


      {/* Toast Notifications Container */}
      <div className="fixed bottom-16 lg:bottom-6 right-4 left-4 sm:left-auto z-50 flex flex-col gap-2.5 max-w-sm font-mono">
        {toasts.map(toast => (
          <div
            key={toast.id}
            className="bg-[#161b22] text-[#f0f6fc] border border-stone-700 shadow-2xl rounded-2xl p-3.5 flex items-start gap-3 animate-slide-in duration-300 backdrop-blur-md"
          >
            <div className={`w-2.5 h-2.5 rounded-full mt-1 flex-shrink-0 animate-ping ${toast.type === 'alert' ? 'bg-amber-400' : 'bg-emerald-400'}`}></div>
            <div>
              <div className="font-extrabold text-xs text-white">
                {toast.type === 'alert' ? '⚠️ Cenové Upozornenie' : '✅ Trhová Aktualizácia'}
              </div>
              <div className="text-[11px] text-stone-300 mt-0.5 font-medium">{toast.message}</div>
            </div>
            <button
              onClick={() => setToasts(prev => prev.filter(t => t.id !== toast.id))}
              className="text-stone-400 hover:text-white ml-auto text-xs font-bold p-1"
            >
              ✕
            </button>
          </div>
        ))}
      </div>

      {/* Application Manual & Mobile Setup Guide Modal (Mounted via Portal) */}
      {showExplanationModal && createPortal(
        <div 
          onClick={() => setShowExplanationModal(false)}
          className="fixed inset-0 bg-slate-950/75 backdrop-blur-md z-[99999] flex items-center justify-center p-3 sm:p-6 animate-[fadeIn_0.2s_ease-out]"
        >
          <div 
            onClick={(e) => e.stopPropagation()}
            className="bg-white border border-slate-200 rounded-3xl max-w-3xl w-full max-h-[90vh] overflow-y-auto shadow-2xl p-5 sm:p-7 space-y-6 font-mono text-xs text-slate-900"
          >
            {/* Modal Header */}
            <div className="flex justify-between items-start border-b border-slate-200/80 pb-4">
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-xl">📖</span>
                  <h2 className="text-base font-black uppercase tracking-tight text-slate-900">
                    {t("explanationTitle")}
                  </h2>
                </div>
                <p className="text-[11px] text-slate-500 font-medium mt-1">{t("explanationDesc")}</p>
              </div>
              <button
                onClick={() => setShowExplanationModal(false)}
                className="text-slate-400 hover:text-slate-900 border border-slate-200 hover:border-slate-400 px-3 py-1.5 rounded-xl transition-all font-bold hover:bg-slate-100 shadow-2xs"
              >
                {t("closeBtn").toUpperCase()} [✕]
              </button>
            </div>

            {/* Segmented Guide Tabs */}
            <div className="flex flex-wrap gap-1.5 p-1 bg-slate-100 rounded-2xl border border-slate-200 shadow-inner">
              <button
                onClick={() => setManualModalTab('features')}
                className={`flex-1 min-w-[120px] py-2 px-3 rounded-xl text-xs font-black transition-all flex items-center justify-center gap-1.5 ${
                  manualModalTab === 'features'
                    ? 'bg-slate-900 text-white shadow-sm'
                    : 'text-slate-600 hover:text-slate-950 hover:bg-slate-200/60'
                }`}
              >
                <span>💡</span>
                <span>{language === 'sk' ? 'Funkcie Aplikácie' : 'Feature Manual'}</span>
              </button>

              <button
                onClick={() => setManualModalTab('ios')}
                className={`flex-1 min-w-[120px] py-2 px-3 rounded-xl text-xs font-black transition-all flex items-center justify-center gap-1.5 ${
                  manualModalTab === 'ios'
                    ? 'bg-slate-900 text-white shadow-sm'
                    : 'text-slate-600 hover:text-slate-950 hover:bg-slate-200/60'
                }`}
              >
                <span>🍏</span>
                <span>{language === 'sk' ? 'Inštalácia iPhone (iOS)' : 'iPhone / iOS Setup'}</span>
              </button>

              <button
                onClick={() => setManualModalTab('android')}
                className={`flex-1 min-w-[120px] py-2 px-3 rounded-xl text-xs font-black transition-all flex items-center justify-center gap-1.5 ${
                  manualModalTab === 'android'
                    ? 'bg-slate-900 text-white shadow-sm'
                    : 'text-slate-600 hover:text-slate-950 hover:bg-slate-200/60'
                }`}
              >
                <span>🤖</span>
                <span>{language === 'sk' ? 'Inštalácia Android' : 'Android Setup'}</span>
              </button>

              <button
                onClick={() => setManualModalTab('desktop')}
                className={`flex-1 min-w-[120px] py-2 px-3 rounded-xl text-xs font-black transition-all flex items-center justify-center gap-1.5 ${
                  manualModalTab === 'desktop'
                    ? 'bg-slate-900 text-white shadow-sm'
                    : 'text-slate-600 hover:text-slate-950 hover:bg-slate-200/60'
                }`}
              >
                <span>💻</span>
                <span>{language === 'sk' ? 'Mac / Windows PWA' : 'Desktop PWA'}</span>
              </button>
            </div>

            {/* TAB CONTENT 1: SYSTEM FEATURES MANUAL */}
            {manualModalTab === 'features' && (
              <div className="space-y-4 animate-[fadeIn_0.2s_ease-out]">
                <div className="bg-slate-50 p-4 rounded-2xl border border-slate-200 space-y-1.5">
                  <h4 className="font-black text-slate-900 uppercase text-xs flex items-center gap-2">
                    <span>📊</span>
                    <span>{language === 'sk' ? 'Portfólio & Sektorový Inšpektor' : 'Portfolio & Sector Breakdown'}</span>
                  </h4>
                  <p className="text-slate-600 font-sans leading-relaxed text-xs">
                    {language === 'sk'
                      ? 'Reálne sektorové rozloženie, menové prevody (EUR / USD) s automatickým prepočtom trhových cien z burzy. Kliknutím na akékoľvek aktívum otvoríte inšpektor detailov, nákupných nákladov a váhy v portfóliu.'
                      : 'Real-time economic sector weighting, automated EUR/USD currency conversion, and interactive asset inspector displaying average entry costs, market value, and portfolio concentration.'}
                  </p>
                </div>

                <div className="bg-slate-50 p-4 rounded-2xl border border-slate-200 space-y-1.5">
                  <h4 className="font-black text-slate-900 uppercase text-xs flex items-center gap-2">
                    <span>📈</span>
                    <span>{language === 'sk' ? 'Probability Lattice (Pravdepodobnostná mriežka)' : 'Probability Lattice'}</span>
                  </h4>
                  <p className="text-slate-600 font-sans leading-relaxed text-xs">
                    {language === 'sk'
                      ? 'Binomický a trinomický simulátor vývoja cien (analógia Galtonovej dosky). Generuje pravdepodobnostné cesty ceny naprieč úrovňami volatility. "Landed Green" ukazuje podiel ciest nad nákupnou cenou, "EV/Trade" integruje očakávanú hodnotu zisku.'
                      : 'Binomial / trinomial price tree simulator testing future trajectories through volatility gates. "Landed Green" shows the fraction of paths expiring in profit above cost basis.'}
                  </p>
                </div>

                <div className="bg-slate-50 p-4 rounded-2xl border border-slate-200 space-y-1.5">
                  <h4 className="font-black text-slate-900 uppercase text-xs flex items-center gap-2">
                    <span>⛰️</span>
                    <span>{language === 'sk' ? 'Tail Probability Ridge (Chvostové riziko)' : 'Tail Probability Ridge'}</span>
                  </h4>
                  <p className="text-slate-600 font-sans leading-relaxed text-xs">
                    {language === 'sk'
                      ? 'Modeluje extrémne a mimoriadne trhové výkyvy (fat-tailed distributions). Umožňuje detegovať asymetriu a potenciál viacnásobného výnosu verzus riziko hlbokého prepadu.'
                      : 'Visualizes option strike landscape boundaries and scans for outlier, fat-tailed pricing anomalies across skew bounds.'}
                  </p>
                </div>

                <div className="bg-slate-50 p-4 rounded-2xl border border-slate-200 space-y-1.5">
                  <h4 className="font-black text-slate-900 uppercase text-xs flex items-center gap-2">
                    <span>🎲</span>
                    <span>{language === 'sk' ? 'Monte Carlo Simulácia & Výhľad' : 'Monte Carlo Simulation & Projections'}</span>
                  </h4>
                  <p className="text-slate-600 font-sans leading-relaxed text-xs">
                    {language === 'sk'
                      ? 'Generuje tisíce paralelných trajektórií podľa geometrického Brownovho pohybu. Poskytuje presné kvantitatívne odhady Value-at-Risk (VaR 95%) a očakávaný ročný koridor výnosov (10., 50. a 90. percentil).'
                      : 'Generates thousands of price pathways under geometric Brownian motion, computing Value-at-Risk (VaR 95%) and compounding percentile bands (10th, median, 90th).'}
                  </p>
                </div>

                <div className="bg-slate-50 p-4 rounded-2xl border border-slate-200 space-y-1.5">
                  <h4 className="font-black text-slate-900 uppercase text-xs flex items-center gap-2">
                    <span>📧</span>
                    <span>{language === 'sk' ? 'Automatický IMAP Import Pokynov' : 'Automated IMAP Import'}</span>
                  </h4>
                  <p className="text-slate-600 font-sans leading-relaxed text-xs">
                    {language === 'sk'
                      ? 'Pripája sa k zabezpečenej poštovej schránke, automaticky číta PDF konfirmácie o nákupoch a predajoch od bánk a brokerov, dešifruje ich a zapisuje transakcie do účtovnej knihy bez nutnosti manuálneho prepisovania.'
                      : 'Monitors configured secure mailboxes, extracts trade execution confirmation PDFs, decrypts statements, and registers positions directly into the ledger.'}
                  </p>
                </div>
              </div>
            )}

            {/* TAB CONTENT 2: IPHONE / IOS SETUP */}
            {manualModalTab === 'ios' && (
              <div className="space-y-4 animate-[fadeIn_0.2s_ease-out]">
                <div className="p-4 rounded-2xl bg-indigo-50/70 border border-indigo-200 space-y-2">
                  <div className="flex items-center gap-2 text-indigo-900 font-black text-xs">
                    <span>🍏</span>
                    <span>{language === 'sk' ? 'Inštalácia aplikácie na iPhone (Safari PWA)' : 'Installing on iPhone (Safari PWA)'}</span>
                  </div>
                  <p className="text-slate-600 font-sans text-xs leading-relaxed">
                    {language === 'sk'
                      ? 'Aplikácia Anogi Traders je plnohodnotná Progressive Web App (PWA). Na iPhone funguje ako natívna aplikácia bez nutnosti inštalácie z App Store, beží na celú obrazovku a ukladá dáta do bezpečného lokálneho úložiska.'
                      : 'Anogi Traders is a progressive web application that runs in full-screen standalone mode without requiring App Store downloads.'}
                  </p>
                </div>

                <div className="space-y-3 font-sans">
                  <div className="flex items-start gap-3 p-3.5 bg-slate-50 rounded-2xl border border-slate-200">
                    <span className="w-6 h-6 rounded-full bg-slate-900 text-white font-mono font-black text-xs flex items-center justify-center shrink-0">1</span>
                    <div className="text-xs space-y-1">
                      <div className="font-bold text-slate-900">{language === 'sk' ? 'Otvorte Safari na iPhone' : 'Open Safari on your iPhone'}</div>
                      <div className="text-slate-600">{language === 'sk' ? 'Prejdite na adresu https://invest.anogilimited.com (Apple vyžaduje prehliadač Safari pre pridanie na plochu).' : 'Navigate to https://invest.anogilimited.com (Apple requires Safari for home screen installations).'}</div>
                    </div>
                  </div>

                  <div className="flex items-start gap-3 p-3.5 bg-slate-50 rounded-2xl border border-slate-200">
                    <span className="w-6 h-6 rounded-full bg-slate-900 text-white font-mono font-black text-xs flex items-center justify-center shrink-0">2</span>
                    <div className="text-xs space-y-1">
                      <div className="font-bold text-slate-900">{language === 'sk' ? 'Kliknite na tlačidlo "Zdieľať" (Share)' : 'Tap the Share Button'}</div>
                      <div className="text-slate-600 flex items-center gap-1.5 flex-wrap">
                        <span>{language === 'sk' ? 'V spodnej lište Safari klepnite na ikonu štvorca so šípkou nahor' : 'In the bottom toolbar of Safari, tap the square with an upward arrow'}</span>
                        <span className="px-2 py-0.5 rounded bg-slate-200 font-mono font-bold text-[10px]">⎋ Share</span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-start gap-3 p-3.5 bg-slate-50 rounded-2xl border border-slate-200">
                    <span className="w-6 h-6 rounded-full bg-slate-900 text-white font-mono font-black text-xs flex items-center justify-center shrink-0">3</span>
                    <div className="text-xs space-y-1">
                      <div className="font-bold text-slate-900">{language === 'sk' ? 'Vyberte "Pridať na plochu" (Add to Home Screen)' : 'Select "Add to Home Screen"'}</div>
                      <div className="text-slate-600">{language === 'sk' ? 'Zrolujte v ponuke nižšie a zvoľte možnosť s ikonou plus [+].' : 'Scroll down in the action sheet and select "Add to Home Screen" with the [+] icon.'}</div>
                    </div>
                  </div>

                  <div className="flex items-start gap-3 p-3.5 bg-slate-50 rounded-2xl border border-slate-200">
                    <span className="w-6 h-6 rounded-full bg-slate-900 text-white font-mono font-black text-xs flex items-center justify-center shrink-0">4</span>
                    <div className="text-xs space-y-1">
                      <div className="font-bold text-slate-900">{language === 'sk' ? 'Potvrďte kliknutím na "Pridať" (Add)' : 'Confirm by tapping "Add"'}</div>
                      <div className="text-slate-600">{language === 'sk' ? 'Na domovskej obrazovke sa vytvorí natívna ikona Anogi Traders. Po jej spustení beží aplikácia bez adresného riadku v pohlcujúcom zobrazení.' : 'Anogi Traders icon will be added to your home screen, launching in dedicated full-screen without Safari browser bars.'}</div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* TAB CONTENT 3: ANDROID SETUP */}
            {manualModalTab === 'android' && (
              <div className="space-y-4 animate-[fadeIn_0.2s_ease-out]">
                <div className="p-4 rounded-2xl bg-emerald-50/70 border border-emerald-200 space-y-2">
                  <div className="flex items-center gap-2 text-emerald-900 font-black text-xs">
                    <span>🤖</span>
                    <span>{language === 'sk' ? 'Inštalácia aplikácie na Android (Chrome / Brave / Edge)' : 'Installing on Android (Chrome / Brave / Edge)'}</span>
                  </div>
                  <p className="text-slate-600 font-sans text-xs leading-relaxed">
                    {language === 'sk'
                      ? 'V systéme Android je inštalácia automatizovaná vďaka podpore WebAPK s natívnymi notifikáciami a plnou podporou offline režimu.'
                      : 'On Android, WebAPK installation provides native notifications, fast performance, and an integrated app drawer experience.'}
                  </p>
                </div>

                <div className="space-y-3 font-sans">
                  <div className="flex items-start gap-3 p-3.5 bg-slate-50 rounded-2xl border border-slate-200">
                    <span className="w-6 h-6 rounded-full bg-slate-900 text-white font-mono font-black text-xs flex items-center justify-center shrink-0">1</span>
                    <div className="text-xs space-y-1">
                      <div className="font-bold text-slate-900">{language === 'sk' ? 'Otvorte Google Chrome alebo Brave' : 'Open Chrome or Brave'}</div>
                      <div className="text-slate-600">{language === 'sk' ? 'Prejdite na adresu https://invest.anogilimited.com a prihláste sa do svojho účtu.' : 'Navigate to https://invest.anogilimited.com and log in.'}</div>
                    </div>
                  </div>

                  <div className="flex items-start gap-3 p-3.5 bg-slate-50 rounded-2xl border border-slate-200">
                    <span className="w-6 h-6 rounded-full bg-slate-900 text-white font-mono font-black text-xs flex items-center justify-center shrink-0">2</span>
                    <div className="text-xs space-y-1">
                      <div className="font-bold text-slate-900">{language === 'sk' ? 'Kliknite na ponuku prehliadača (3 bodky)' : 'Tap Browser Menu (3 Dots)'}</div>
                      <div className="text-slate-600">{language === 'sk' ? 'V pravom hornom rohu klepnite na ikonu troch zvislých bodiek [⋮].' : 'Tap the three vertical dots in the top right corner.'}</div>
                    </div>
                  </div>

                  <div className="flex items-start gap-3 p-3.5 bg-slate-50 rounded-2xl border border-slate-200">
                    <span className="w-6 h-6 rounded-full bg-slate-900 text-white font-mono font-black text-xs flex items-center justify-center shrink-0">3</span>
                    <div className="text-xs space-y-1">
                      <div className="font-bold text-slate-900">{language === 'sk' ? 'Zvoľte "Inštalovať aplikáciu" alebo "Pridať na plochu"' : 'Select "Install app" or "Add to Home screen"'}</div>
                      <div className="text-slate-600">{language === 'sk' ? 'Kliknite na možnosť "Inštalovať aplikáciu Anogi Traders" a potvrďte dialóg.' : 'Tap "Install Anogi Traders" and confirm the system dialog.'}</div>
                    </div>
                  </div>

                  {/* Step 4: Play Protect Troubleshooting */}
                  <div className="p-3.5 bg-amber-50/80 rounded-2xl border border-amber-200 space-y-2">
                    <div className="flex items-center gap-2 text-amber-900 font-bold text-xs">
                      <span>🛡️</span>
                      <span>{language === 'sk' ? 'Dôležité: Ak Google Play blokuje inštaláciu (Play Protect)' : 'Troubleshooting: If Play Protect blocks installation'}</span>
                    </div>
                    <div className="text-slate-700 text-xs space-y-1.5 leading-relaxed font-sans">
                      <p>
                        {language === 'sk'
                          ? 'V prípade, že telefón upozorní na neznámy zdroj alebo zablokuje inštaláciu cez službu Google Play Protect:'
                          : 'If Android displays a security warning or blocks the external installation via Google Play Protect:'}
                      </p>
                      <ol className="list-decimal list-inside space-y-1 text-slate-800 bg-white/70 p-2.5 rounded-xl border border-amber-200/60 font-mono text-[11px]">
                        <li>{language === 'sk' ? 'Otvorte aplikáciu Google Play na telefóne.' : 'Open Google Play Store on your phone.'}</li>
                        <li>{language === 'sk' ? 'Vpravo hore klepnite na ikonu svojho profilu (profilová fotka).' : 'Tap your profile icon in the top right corner.'}</li>
                        <li>{language === 'sk' ? 'V menu vyberte možnosť "Play Protect".' : 'Select "Play Protect" from the menu.'}</li>
                        <li>{language === 'sk' ? 'Vpravo hore klepnite na ikonu ozubeného kolieska ⚙️ (Nastavenia).' : 'Tap the gear icon ⚙️ (Settings) in the top right.'}</li>
                        <li>{language === 'sk' ? 'Vypnite: "Zlepšiť detekciu škodlivých aplikácií" (Posielať neznáme aplikácie do Googlu), prípadne dočasne vypnite "Kontrolovať aplikácie pomocou služby Play Protect".' : 'Toggle OFF: "Improve harmful app detection", or temporarily disable "Scan apps with Play Protect".'}</li>
                        <li>{language === 'sk' ? 'Vráťte sa do prehliadača a dokončite inštaláciu Anogi Traders.' : 'Return to browser and finish installing Anogi Traders.'}</li>
                      </ol>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* TAB CONTENT 4: MAC / WINDOWS PWA */}
            {manualModalTab === 'desktop' && (
              <div className="space-y-4 animate-[fadeIn_0.2s_ease-out]">
                <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200 space-y-2">
                  <div className="flex items-center gap-2 text-slate-900 font-black text-xs">
                    <span>💻</span>
                    <span>{language === 'sk' ? 'Samostatné okno aplikácie na Macu & Windows' : 'Standalone Desktop App (macOS & Windows)'}</span>
                  </div>
                  <p className="text-slate-600 font-sans text-xs leading-relaxed">
                    {language === 'sk'
                      ? 'Anogi Traders môžete používať ako samostatnú aplikáciu na počítači v Docku alebo paneli úloh (Chrome, Brave, Safari na macOS Sonoma).'
                      : 'Run Anogi Traders in its own desktop window directly from your macOS Dock or Windows Taskbar.'}
                  </p>
                </div>

                <div className="space-y-3 font-sans">
                  <div className="p-3.5 bg-slate-50 rounded-2xl border border-slate-200 space-y-1.5 text-xs">
                    <div className="font-bold text-slate-900 flex items-center gap-2">
                      <span>🌐</span>
                      <span>Google Chrome / Brave / Edge</span>
                    </div>
                    <p className="text-slate-600">
                      {language === 'sk'
                        ? 'V adresnom riadku vpravo kliknite na ikonu monitora so šípkou (alebo v menu: "Uložiť a zdieľať" -> "Inštalovať Anogi Traders"). Aplikácia sa otvorí vo vlastnom okne bez kariet prehliadača.'
                        : 'Click the install monitor icon on the right side of the address bar, or go to Menu -> Save and share -> Install page as app.'}
                    </p>
                  </div>

                  <div className="p-3.5 bg-slate-50 rounded-2xl border border-slate-200 space-y-1.5 text-xs">
                    <div className="font-bold text-slate-900 flex items-center gap-2">
                      <span>🍎</span>
                      <span>Safari (macOS Sonoma a novšie)</span>
                    </div>
                    <p className="text-slate-600">
                      {language === 'sk'
                        ? 'V hornom menu zvoľte Súbor (File) -> Pridať do Docku (Add to Dock). Aplikácia sa pridá medzi vaše Mac aplikácie.'
                        : 'Go to File -> Add to Dock in the top menu bar to place Anogi Traders directly into your macOS Launchpad and Dock.'}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Modal Footer */}
            <div className="border-t border-slate-200/80 pt-4 flex flex-col sm:flex-row justify-between items-center gap-3">
              <div className="text-[10px] text-slate-500 font-mono">
                Anogi Limited • Anogi Traders Engine v1.0.0
              </div>
              <button
                onClick={() => setShowExplanationModal(false)}
                className="w-full sm:w-auto bg-slate-900 text-white px-6 py-2.5 rounded-xl font-black hover:bg-slate-800 hover:shadow-md transition-all uppercase tracking-wider text-xs"
              >
                {t("understoodBtn").toUpperCase()}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
      </div>

      {/* MOBILE BOTTOM NAVIGATION BAR (Phones < 1024px) */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 z-40 bg-[var(--bg-secondary)]/95 backdrop-blur-md border-t border-[var(--border-color)] px-2 py-1.5 flex items-center justify-around font-mono text-[9px] shadow-lg pb-[calc(0.375rem+env(safe-area-inset-bottom,0px))]">
        <button
          onClick={() => { setActiveTab('trading'); setMobileMenuOpen(false); }}
          className={`flex flex-col items-center gap-0.5 p-1.5 rounded-xl transition-all ${
            activeTab === 'trading' ? 'text-indigo-600 font-black' : 'text-stone-500 font-bold'
          }`}
        >
          <span className="text-base">💼</span>
          <span>{language === 'sk' ? 'Portfólio' : 'Portfolio'}</span>
        </button>

        <button
          onClick={() => { setActiveTab('charts'); setMobileMenuOpen(false); }}
          className={`flex flex-col items-center gap-0.5 p-1.5 rounded-xl transition-all ${
            activeTab === 'charts' ? 'text-indigo-600 font-black' : 'text-stone-500 font-bold'
          }`}
        >
          <span className="text-base">📈</span>
          <span>{language === 'sk' ? 'Grafy' : 'Charts'}</span>
        </button>

        <button
          onClick={() => { setActiveTab('quant'); setMobileMenuOpen(false); }}
          className={`flex flex-col items-center gap-0.5 p-1.5 rounded-xl transition-all ${
            activeTab === 'quant' ? 'text-indigo-600 font-black' : 'text-stone-500 font-bold'
          }`}
        >
          <span className="text-base">🏛️</span>
          <span>{language === 'sk' ? 'Kvant' : 'Quant'}</span>
        </button>

        <button
          onClick={() => { setActiveTab('wealth'); setMobileMenuOpen(false); }}
          className={`flex flex-col items-center gap-0.5 p-1.5 rounded-xl transition-all ${
            activeTab === 'wealth' ? 'text-indigo-600 font-black' : 'text-stone-500 font-bold'
          }`}
        >
          <span className="text-base">💎</span>
          <span>{language === 'sk' ? 'Dane' : 'Wealth'}</span>
        </button>

        <button
          onClick={() => setMobileMenuOpen(prev => !prev)}
          className={`flex flex-col items-center gap-0.5 p-1.5 rounded-xl transition-all ${
            mobileMenuOpen ? 'text-indigo-600 font-black' : 'text-stone-500 font-bold'
          }`}
        >
          <span className="text-base">☰</span>
          <span>Menu</span>
        </button>
      </nav>
    </div>
  );
};
